/*
 * Copyright (c) Arc XP 2025.
 */

import { WaveformData } from '@wpmedia/swordfish-ts';

/**
 * <waveform> custom element
 */
export class WaveformElement extends HTMLElement {
  data: WaveformData = {} as WaveformData;
  #canvas!: HTMLCanvasElement;
  #ctx!: CanvasRenderingContext2D;
  #shadow!: ShadowRoot;
  #resizeObserver?: ResizeObserver;
  #currentTime: number = 0;
  #duration: number = 0;
  #boundClickHandler?: (event: MouseEvent) => void;

  constructor() {
    super();
    this.#shadow = this.attachShadow({ mode: 'open' });
    const style = document.createElement('style');
    style.textContent = `
      :host {
        display: block;
        width: var(--waveform-width, 100%);
        height: var(--waveform-height, 100%);
        --waveform-background-color: black;
        --waveform-center-line-color: transparent;
        --waveform-played-color: #0088cc;
        --waveform-unplayed-color: #666666;
        --waveform-bar-thickness: 0.5;
      }
      canvas {
        display: block;
        width: 100%;
        height: 100%;
        aspect-ratio: var(--waveform-aspect-ratio, auto);
      }
    `;

    this.#shadow.appendChild(style);
    this.#canvas = document.createElement('canvas');
    this.#ctx = this.#canvas.getContext('2d')!;
    this.#shadow.appendChild(this.#canvas);
  }

  connectedCallback(): void {
    const raw = this.getAttribute('data');
    try {
      const parsed = raw && raw !== 'undefined' ? JSON.parse(raw) : null;
      this.data = (parsed || {}) as WaveformData;
    } catch {
      this.data = {} as WaveformData;
    }

    this.#resizeObserver = new ResizeObserver(() => {
      this.#render();
    });
    this.#resizeObserver.observe(this);

    // Add click handler for seeking
    this.#boundClickHandler = this.#handleClick.bind(this);
    this.#canvas.addEventListener('click', this.#boundClickHandler);

    // Add cursor pointer to indicate clickability
    this.#canvas.style.cursor = 'pointer';

    this.#render();
  }

  disconnectedCallback(): void {
    if (this.#resizeObserver) {
      this.#resizeObserver.disconnect();
      this.#resizeObserver = undefined;
    }

    // Remove click handler
    if (this.#boundClickHandler) {
      this.#canvas.removeEventListener('click', this.#boundClickHandler);
      this.#boundClickHandler = undefined;
    }
  }

  /**
   * Update the current playback time
   */
  updateTime(currentTime: number, duration: number = 0): void {
    this.#currentTime = currentTime;
    if (duration > 0) {
      this.#duration = duration;
    }
    this.#render();
  }

  /**
   * Get the current playback progress as a ratio (0-1)
   */
  get progress(): number {
    return this.#duration > 0 ? this.#currentTime / this.#duration : 0;
  }

  /**
   * Get a CSS custom property value from the component
   */
  #getCSSVariable(name: string): string {
    return getComputedStyle(this).getPropertyValue(name).trim();
  }

  /**
   * Handle click events on the waveform canvas
   */
  #handleClick(event: MouseEvent): void {
    if (this.#duration <= 0) return;

    const rect = this.#canvas.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const canvasWidth = rect.width;

    // Calculate the clicked position as a ratio (0-1)
    const clickRatio = Math.max(0, Math.min(1, x / canvasWidth));

    // Calculate the target time
    const targetTime = clickRatio * this.#duration;

    // Dispatch custom event with the target time
    this.dispatchEvent(
      new CustomEvent('waveform-seek', {
        detail: {
          targetTime,
          ratio: clickRatio,
        },
        bubbles: true,
      })
    );
  }

  #render(): void {
    const dpr = Math.max(1, window.devicePixelRatio || 1);
    const padding = 20;

    const canvasRect = this.#canvas.getBoundingClientRect();
    const hostRect = this.getBoundingClientRect();

    const cssWidth = Math.max(1, Math.round(canvasRect.width || hostRect.width || 1));

    const cssHeight = Math.max(1, Math.round(canvasRect.height || hostRect.height || cssWidth / 8));

    const targetW = Math.ceil(cssWidth * dpr);
    const targetH = Math.ceil(cssHeight * dpr);
    if (this.#canvas.width !== targetW || this.#canvas.height !== targetH) {
      this.#canvas.width = targetW;
      this.#canvas.height = targetH;
    }

    this.#canvas.style.width = '100%';
    this.#canvas.style.height = '100%';

    this.#ctx.setTransform(1, 0, 0, 1, 0, 0);
    this.#ctx.scale(dpr, dpr);

    this.#ctx.clearRect(0, 0, cssWidth, cssHeight);
    this.#ctx.fillStyle = this.#getCSSVariable('--waveform-background-color') || 'black';
    this.#ctx.fillRect(0, 0, cssWidth, cssHeight);

    const mins = Array.isArray(this.data.min) ? this.data.min : [];
    const maxs = Array.isArray(this.data.max) ? this.data.max : [];
    const bins = Math.min(mins.length, maxs.length);
    if (!bins) {
      return;
    }

    const centerY = padding + (cssHeight - 2 * padding) / 2;
    const halfHeight = Math.max(1, (cssHeight - 2 * padding) / 2);

    this.#ctx.save();
    this.#ctx.strokeStyle = this.#getCSSVariable('--waveform-center-line-color') || '#666';
    this.#ctx.lineWidth = 1;
    this.#ctx.beginPath();
    this.#ctx.moveTo(0, centerY);
    this.#ctx.lineTo(cssWidth, centerY);
    this.#ctx.stroke();
    this.#ctx.restore();

    const binWidth = cssWidth / bins;
    const xForBinCenter = (i: number) => i * binWidth + binWidth / 2;

    this.#ctx.globalAlpha = 0.9;
    this.#ctx.lineCap = 'round';

    // Get configurable bar thickness from CSS variable
    const thicknessMultiplier = parseFloat(this.#getCSSVariable('--waveform-bar-thickness')) || 0.5;
    this.#ctx.lineWidth = Math.max(0.5, binWidth * thicknessMultiplier);

    const INT16_DEN = 32768;
    const progress = this.progress;

    for (let i = 0; i < bins; i++) {
      const x = xForBinCenter(i);
      const binProgress = (i + 0.5) / bins; // Progress at this bin position

      const yminI16 = Number.isFinite(mins[i] as number) ? (mins[i] as number) : 0;
      const ymaxI16 = Number.isFinite(maxs[i] as number) ? (maxs[i] as number) : 0;

      const yminN = clamp((yminI16 as number) / INT16_DEN, -1, 1);
      const ymaxN = clamp((ymaxI16 as number) / INT16_DEN, -1, 1);

      let y1 = centerY - yminN * halfHeight;
      let y2 = centerY - ymaxN * halfHeight;

      if (Math.abs(y1 - y2) < 0.5) {
        const mid = (y1 + y2) / 2;
        y1 = mid - 0.5;
        y2 = mid + 0.5;
      }

      // Color based on playback progress
      if (binProgress <= progress) {
        // Played portion - use a brighter color (blue/teal)
        this.#ctx.strokeStyle = this.#getCSSVariable('--waveform-played-color') || '#0088cc';
      } else {
        // Unplayed portion - use gray
        this.#ctx.strokeStyle = this.#getCSSVariable('--waveform-unplayed-color') || '#666666';
      }

      this.#ctx.beginPath();
      this.#ctx.moveTo(x, y1);
      this.#ctx.lineTo(x, y2);
      this.#ctx.stroke();
    }
  }
}

function clamp(v: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, v));
}

declare global {
  interface HTMLElementTagNameMap {
    waveform: WaveformElement;
  }
}

if (!customElements.get('koi-waveform')) {
  customElements.define('koi-waveform', WaveformElement);
}
